import { onBeforeUnmount, watch, ref } from 'vue'
import type { Ref } from 'vue'

export const useOnScrollable = (
  scrollableContainer: Ref<HTMLElement | null>,
  loading: Ref<boolean>,
  savedScrollPosition: number | null
): Ref<boolean> => {
  let observer: MutationObserver | null = null
  let calledForEl: HTMLElement | null = null
  let observeInterval: ReturnType<typeof setInterval> | null = null
  const isScrollable: Ref<boolean> = ref(false)

  // Disconnects the observer and interval to stop watching for scrollability.
  function stopObserving(): void {
    if (observer) {
      observer.disconnect()
      observer = null
    }
    if (observeInterval !== null) {
      clearInterval(observeInterval)
      observeInterval = null
    }
  }

  // Checks if the element is scrollable and can reach the saved scroll position, then calls the callback.
  function scrollableObserve(el: HTMLElement): void {
    const canScrollToSavedPosition =
      el.scrollHeight >= (savedScrollPosition ?? 0)

    if (
      el.scrollHeight > el.clientHeight &&
      (!savedScrollPosition || canScrollToSavedPosition)
    ) {
      calledForEl = el
      stopObserving()
      isScrollable.value = true
    }
  }

  // Watch for changes to the scrollable container and loading state, and set up the observer when ready.
  watch(
    [scrollableContainer, loading],
    ([el, isLoading]) => {
      if (el && el === calledForEl) return
      if (el && !isLoading) {
        isScrollable.value = false
        observer?.disconnect()
        observer = new MutationObserver(() => {
          if (el !== calledForEl) scrollableObserve(el)
        })
        observer.observe(el, { childList: true, subtree: true })
        // this is in case it is already scrollable and the mutation observer won't trigger,
        // we want to call the callback immediately
        scrollableObserve(el)

        if (observeInterval !== null) {
          clearInterval(observeInterval)
          observeInterval = null
        }

        // In some cases, the container might not reach it's final scrollHeight through mutations,
        // so we also set an interval to check for that.
        observeInterval = setInterval(() => {
          if (calledForEl !== null) {
            if (observeInterval !== null) {
              clearInterval(observeInterval)
              observeInterval = null
            }
            return
          }
          scrollableObserve(el)
        }, 100)
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    stopObserving()
  })

  return isScrollable
}
